import React from "react";

import { startCase } from "lodash-es";

import { extractEntityNameFromRef } from "src/jsonSchema/schemaUtils";
import { isArrayOfRef, isObject, JsonSchema } from "src/jsonSchema/types";
import { TreeRenderer } from "src/jsonSchema/viewer/TreeRenderer";
import {
  buildViewModelTree,
  UnionChoice,
  ViewModelNode,
} from "src/jsonSchema/viewer/viewModelTreeBuilder";

type JSONSchemaRendererProps = {
  schema: JsonSchema;
  basePath?: string;
  maxDepth?: number;
  displayOptions?: Partial<JSONSchemaViewerDisplayOptions>;
  openJsonSchemaWindow?: () => void;
};

export type JSONSchemaViewerDisplayOptions = {
  // - Type labels

  // Whether to display type labels at all
  showTypeLabels: boolean;
  // Whether to display type labels for primitive properties
  showPrimitiveTypeLabels: boolean;
  // Whether to display required asterisks (*) for required properties
  showRequired: boolean;

  // - Paths

  // Whether to display full data paths
  showDataPaths: boolean;
  // Whether to display full data paths for primitive properties
  showPrimitiveDataPath: boolean;
  // Configurable path prefix (e.g., "data", "params", "$")
  pathPrefix: string;
  // Ellipsize paths when nesting exceeds this depth (0 = no ellipsizing)
  pathEllipsizeDepth: number;

  // - Expansion

  // Auto-expand properties until this depth (0 = no auto-expand)
  autoExpandDepth: number;
  // Maximum depth for rendering nested properties (prevents infinite recursion)
  maxDepth: number;
  // Whether to allow collapsing properties
  canCollapse: boolean;

  // - Custom Rendering

  customLabelAndTypeRenderer?: (
    node: ViewModelNode,
    defaultRenderer: React.ReactNode,
    selectedChoice?: UnionChoice,
  ) => React.ReactNode;
};

export const defaultDisplayOptions: JSONSchemaViewerDisplayOptions = {
  showTypeLabels: true,
  showPrimitiveTypeLabels: false,
  showRequired: true,
  showDataPaths: true,
  showPrimitiveDataPath: false,
  pathEllipsizeDepth: 5,
  pathPrefix: "data",
  autoExpandDepth: 1,
  maxDepth: 10,
  canCollapse: true,
};

export const JSONSchemaRenderer: React.FC<JSONSchemaRendererProps> = ({
  schema,
  basePath,
  maxDepth = 10,
  openJsonSchemaWindow,
  displayOptions = {},
}) => {
  if (!schema?.properties || Object.keys(schema.properties).length === 0) {
    return (
      <div className="p-4 font-inter-normal-13px text-gray-500 italic">
        No properties found in schema
      </div>
    );
  }

  const mergedDisplayOptions = { ...defaultDisplayOptions, ...displayOptions };
  const actualBasePath = basePath || mergedDisplayOptions.pathPrefix;

  // Update the display options with the actual base path and maxDepth from props
  const finalDisplayOptions = {
    ...mergedDisplayOptions,
    pathPrefix: actualBasePath,
    maxDepth: maxDepth,
  };

  // Build the view model tree upfront
  const viewModelTree = buildViewModelTree(schema, finalDisplayOptions);

  return (
    <TreeRenderer
      displayOptions={finalDisplayOptions}
      openJsonSchemaWindow={openJsonSchemaWindow}
      tree={viewModelTree}
    />
  );
};

// - Alternative renderer

export const createTitleOnlyHeaderRenderer = () => {
  return (
    node: ViewModelNode,
    defaultRenderer: React.ReactNode,
    selectedChoice?: UnionChoice,
  ): React.ReactNode | null => {
    // Determine the currently active schema node to check
    const currentSchemaNode = selectedChoice?.schemaNode || node.property;
    const isCurrentRef = selectedChoice?.node?.isRef ?? node.isRef;

    const shouldUseCustomRenderer = (): boolean => {
      if (isCurrentRef || currentSchemaNode.$ref) return true;
      if (isObject(currentSchemaNode)) return true;
      return isArrayOfRef(currentSchemaNode);
    };

    if (!shouldUseCustomRenderer()) {
      return defaultRenderer;
    }

    let title = currentSchemaNode.title;
    if (!title) {
      if (currentSchemaNode.$ref) {
        title = extractEntityNameFromRef(currentSchemaNode.$ref);
      } else {
        title = startCase(node.name);
      }
    }

    return (
      <span className="font-inter-medium-13px text-gray-800">{title}</span>
    );
  };
};
